import './App.css';
import { SpeedInsights } from "@vercel/speed-insights/react"
import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import SeveranceCalculatorForm from "./SeveranceCalculator/SeveranceCalculatorForm";
import SocialShare from "./SocialShare/SocialShare";
import Disclaimer from "./Disclaimer/Disclaimer";
import TermsAcceptance from "./Disclaimer/TermsAcceptance";
import { Helmet } from "react-helmet";

// Import Article Components
import ArticlesHub from "./Articles/ArticlesHub";
import RifHistory from "./Articles/RifHistory";
import SeveranceExplained from "./Articles/SeveranceExplained";
import RifSurvivalGuide from "./Articles/RifSurvivalGuide";

// Import News Feed Component
import NewsFeed from "./NewsFeed/NewsFeed";
import ArticleViewer from "./ArticleViewer/ArticleViewer";

import {
    createTheme,
    CssBaseline,
    Box,
    ThemeProvider,
    Button,
    Typography,
    Grid
} from "@mui/material";


const darkTheme = createTheme({
    palette: {
        mode: 'dark',
    },
});

// Main Calculator Component
const Calculator = ({ termsAccepted }) => {
    return (
        <>
            {termsAccepted && (
                <>
                    <SeveranceCalculatorForm />
                    <Box sx={{ my: 2, textAlign: 'center' }}>
                        <Button 
                            component={Link} 
                            to="/articles" 
                            variant="outlined" 
                            color="primary"
                            sx={{ my: 1 }}
                        >
                            View RIF & Severance Articles
                        </Button>
                    </Box>
                    <SocialShare />
                    <Disclaimer />
                </>
            )}
        </>
    );
};

// Layout component
const AppLayout = ({ termsAccepted }) => {
    const location = useLocation();
    const isViewingExternalNews = location.pathname.startsWith('/news/'); 
    const isViewingInternalArticle = location.pathname.startsWith('/articles/') && location.pathname !== '/articles';
    const isViewingArticlesHub = location.pathname === '/articles';

    return (
        <Box
            component="main"
            aria-label="Federal RIF Severance Calculator Layout"
            sx={{
                minHeight: '100vh',
                backgroundImage: 'url(doge-black.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                p: 2 
            }}
        >
            <Grid container spacing={3} sx={{ maxWidth: 1200 }}> 
                {/* News Feed Column (Always visible on md and up) */}
                <Grid item md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
                    <NewsFeed />
                </Grid>
                
                {/* Main Content Column (Always takes 9 cols on md+ now) */}
                <Grid item xs={12} md={9}> 
                    <Box 
                        component="section"
                        sx={{
                            p:2,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            borderRadius: 1,
                            // Conditional scrolling for external news view
                            ...(isViewingExternalNews && { 
                                height: 'calc(100vh - 32px)',
                                overflowY: 'auto' 
                            })
                        }}
                    >
                        <Routes>
                            <Route path="/" element={<Calculator termsAccepted={termsAccepted} />} />
                            <Route path="/articles" element={<ArticlesHub />} />
                            <Route path="/articles/rif-history" element={<RifHistory />} />
                            <Route path="/articles/severance-explained" element={<SeveranceExplained />} />
                            <Route path="/articles/rif-survival-guide" element={<RifSurvivalGuide />} />
                            <Route path="/news/:encodedUrl" element={<ArticleViewer />} /> 
                        </Routes>
                        
                        {/* Back Button Logic Refined */}

                        {/* Show 'Back to Calculator' when viewing the Articles Hub */}
                        {isViewingArticlesHub && (
                             <Box sx={{ mt: 3, mb:1, textAlign: 'center' }}>
                                <Button component={Link} to="/" variant="contained" color="primary">
                                    Back to Calculator
                                </Button>
                            </Box>
                        )}

                        {/* Show 'Back to Calculator' when viewing an External News Article */}
                        {isViewingExternalNews && (
                             <Box sx={{ mt: 3, mb:1, textAlign: 'center' }}>
                                <Button component={Link} to="/" variant="contained" color="primary">
                                    Back to Calculator
                                </Button>
                            </Box>
                        )}

                        {/* Show 'Back to Articles Hub' when viewing an Internal Article */}
                        {isViewingInternalArticle && (
                             <Box sx={{ mt: 3, mb:1, textAlign: 'center' }}>
                                <Button component={Link} to="/articles" variant="contained" color="secondary">
                                    Back to Articles Hub
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

function App() {
    const [termsAccepted, setTermsAccepted] = useState(false);

    const handleTermsAccept = () => {
        setTermsAccepted(true);
    };

    return (
        <Router>
            <div className="App">
                <Helmet>
                    <title>RIF Calculator - Federal Employee Severance Pay Estimator</title>
                </Helmet>
                <SpeedInsights />
                <ThemeProvider theme={darkTheme}>
                    <CssBaseline/>
                    {!termsAccepted && <TermsAcceptance onAccept={handleTermsAccept} />}
                    {termsAccepted && <AppLayout termsAccepted={termsAccepted} />}
                </ThemeProvider>
            </div>
        </Router>
    );
}

export default App;
